import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePublishToEmma } from '../hooks/usePublishToEmma';
import portalT from '../translations/portal';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeft, Users, FileText, Target, CheckCircle,
  Loader, Send, Plus, Lightbulb, UserCheck, Briefcase, Star,
  Trash2, GitCompare, X, Upload, File as FileIcon, Pencil
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api/compass/hiring`;

const HiringAssistant = () => {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = portalT[lang]?.common || portalT.en.common;
  
  const [view, setView] = useState('start'); // start, create-role, analysis, compare
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [cvText, setCvText] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [matchScore, setMatchScore] = useState(null);
  
  // Multi-CV comparison state
  const [candidates, setCandidates] = useState([]); // [{id, name, cvText}]
  const [newCandidateName, setNewCandidateName] = useState('');
  const [newCandidateCv, setNewCandidateCv] = useState('');
  const [comparisonResult, setComparisonResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCompare, setUploadingCompare] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const cvFileRef = useRef(null);
  const compareCvFileRef = useRef(null);
  
  // Role creation form
  const [newRole, setNewRole] = useState({
    title: '',
    description: '',
    requirements: '',
    nice_to_have: ''
  });
  
  const chatEndRef = useRef(null);

  const txt = {
    nl: {
      title: 'AI Skills Match',
      subtitle: 'Vind de perfecte match voor je team',
      step1: 'Stap 1: Functie Definieren',
      step1Desc: 'Maak eerst een functieprofiel aan of selecteer een bestaande',
      createRole: 'Nieuwe Functie Aanmaken',
      editRole: 'Functie Bewerken',
      saveChanges: 'Wijzigingen Opslaan',
      existingRoles: 'Bestaande Functies',
      noRoles: 'Nog geen functies aangemaakt — maak je eerste om te beginnen.',
      step2: 'Stap 2: CV Analyseren',
      step2Desc: 'Upload een CV (PDF, Word) of plak de tekst hieronder',
      cvPlaceholder: 'Plak hier het CV of LinkedIn profiel van de kandidaat...',
      analyzeBtn: 'Analyseer Skills Match',
      uploadBtn: 'Upload CV',
      uploadHint: 'PDF, DOCX of TXT',
      uploadDrag: 'Sleep een bestand hierheen',
      uploading: 'Bestand verwerken...',
      orPaste: 'of plak tekst hieronder',
      roleTitle: 'Functietitel',
      roleTitlePlaceholder: 'Bijv: Marketing Manager',
      roleDesc: 'Functiebeschrijving',
      roleDescPlaceholder: 'Beschrijf de functie, verantwoordelijkheden en doelen...',
      requirements: 'Vereiste Skills & Ervaring',
      requirementsPlaceholder: 'Bijv: 3+ jaar ervaring, HBO/WO, ervaring met Google Ads...',
      niceToHave: 'Nice to Have',
      niceToHavePlaceholder: 'Extra pluspunten die niet verplicht zijn...',
      saveRole: 'Functie Opslaan',
      matchScore: 'Match Score',
      suggestions: [
        'Welke skills mist deze kandidaat?',
        'Schrijf 5 interview vragen',
        'Vergelijk met de ideale kandidaat',
        'Wat zijn de ontwikkelpunten?'
      ],
      chatPlaceholder: 'Stel een vraag over de kandidaat...',
      newAnalysis: 'Nieuwe Analyse',
      back: 'Terug',
      // Multi-CV comparison
      compareTitle: 'Kandidaten Vergelijken',
      compareSubtitle: 'Voeg meerdere CVs toe en laat AI de beste kandidaat kiezen',
      addCandidate: 'Kandidaat Toevoegen',
      candidateName: 'Naam kandidaat',
      candidateNamePlaceholder: 'Bijv: Jan de Vries',
      candidateCv: 'CV / Profiel tekst',
      candidateCvPlaceholder: 'Plak hier het CV of LinkedIn profiel...',
      uploadCandidateBtn: 'Upload CV',
      addBtn: 'Toevoegen aan vergelijking',
      candidatesList: 'Kandidaten voor vergelijking',
      noCandidates: 'Voeg minimaal 2 kandidaten toe om te vergelijken',
      compareBtn: 'Vergelijk Kandidaten met AI',
      comparing: 'AI analyseert kandidaten...',
      comparisonResult: 'Vergelijkingsresultaat',
      removeCandidate: 'Verwijder',
      orCompare: '-- of vergelijk meerdere kandidaten --',
      startComparison: 'Meerdere CVs Vergelijken',
      backToStart: 'Terug naar start',
    },
    en: {
      title: 'AI Skills Match',
      subtitle: 'Find the perfect match for your team',
      step1: 'Step 1: Define Role',
      step1Desc: 'First create a job profile or select an existing one',
      createRole: 'Create New Role',
      editRole: 'Edit Role',
      saveChanges: 'Save Changes',
      existingRoles: 'Existing Roles',
      noRoles: 'No roles created yet — create your first one to get started.',
      step2: 'Step 2: Analyze CV',
      step2Desc: 'Upload a CV (PDF, Word) or paste the text below',
      cvPlaceholder: 'Paste the candidate\'s CV or LinkedIn profile here...',
      analyzeBtn: 'Analyze Skills Match',
      uploadBtn: 'Upload CV',
      uploadHint: 'PDF, DOCX or TXT',
      uploadDrag: 'Drop a file here',
      uploading: 'Processing file...',
      orPaste: 'or paste text below',
      roleTitle: 'Job Title',
      roleTitlePlaceholder: 'E.g.: Marketing Manager',
      roleDesc: 'Job Description',
      roleDescPlaceholder: 'Describe the role, responsibilities and goals...',
      requirements: 'Required Skills & Experience',
      requirementsPlaceholder: 'E.g.: 3+ years experience, Bachelor\'s degree, Google Ads experience...',
      niceToHave: 'Nice to Have',
      niceToHavePlaceholder: 'Extra plusses that are not required...',
      saveRole: 'Save Role',
      matchScore: 'Match Score',
      suggestions: [
        'What skills is this candidate missing?',
        'Write 5 interview questions',
        'Compare to the ideal candidate',
        'What are the development areas?'
      ],
      chatPlaceholder: 'Ask a question about the candidate...',
      newAnalysis: 'New Analysis',
      back: 'Back',
      // Multi-CV comparison
      compareTitle: 'Compare Candidates',
      compareSubtitle: 'Add multiple CVs and let AI choose the best candidate',
      addCandidate: 'Add Candidate',
      candidateName: 'Candidate name',
      candidateNamePlaceholder: 'E.g.: John Smith',
      candidateCv: 'CV / Profile text',
      candidateCvPlaceholder: 'Paste the CV or LinkedIn profile here...',
      uploadCandidateBtn: 'Upload CV',
      addBtn: 'Add to comparison',
      candidatesList: 'Candidates for comparison',
      noCandidates: 'Add at least 2 candidates to compare',
      compareBtn: 'Compare Candidates with AI',
      comparing: 'AI is analyzing candidates...',
      comparisonResult: 'Comparison Result',
      removeCandidate: 'Remove',
      orCompare: '-- or compare multiple candidates --',
      startComparison: 'Compare Multiple CVs',
      backToStart: 'Back to start',
    }
  }[lang] || {
    title: 'AI Skills Match',
    subtitle: 'Find the perfect match',
    step1: 'Step 1', step1Desc: '', createRole: 'New Role', existingRoles: 'Roles',
    noRoles: 'No roles', step2: 'Step 2', step2Desc: '', cvPlaceholder: '', analyzeBtn: 'Analyze',
    roleTitle: '', roleTitlePlaceholder: '', roleDesc: '', roleDescPlaceholder: '',
    requirements: '', requirementsPlaceholder: '', niceToHave: '', niceToHavePlaceholder: '',
    saveRole: 'Save', matchScore: 'Score', suggestions: [], chatPlaceholder: '', newAnalysis: 'New',
    back: 'Back', compareTitle: 'Compare', compareSubtitle: '', addCandidate: 'Add',
    candidateName: 'Name', candidateNamePlaceholder: '', candidateCv: 'CV',
    candidateCvPlaceholder: '', addBtn: 'Add', candidatesList: 'Candidates',
    noCandidates: 'Add 2+', compareBtn: 'Compare', comparing: 'Comparing...',
    comparisonResult: 'Result', removeCandidate: 'Remove', orCompare: 'or compare',
    startComparison: 'Compare CVs', backToStart: 'Back',
  };


  const lastAssistant = (Array.isArray(messages) ? messages : []).filter(m => m.role === 'assistant').slice(-1)[0]?.content;
  usePublishToEmma('AI Hiring Assistant', lastAssistant);
  useEffect(() => { fetchRoles(); }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(res.data.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const deleteRole = async (roleId) => {
    try {
      await axios.delete(`${API}/role/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(prev => (Array.isArray(prev) ? prev : []).filter(r => r.role_id !== roleId));
      if (selectedRole?.role_id === roleId) setSelectedRole(null);
    } catch (err) {
      console.error('Error deleting role:', err);
    }
  };

  const createRole = async () => {
    if (!((newRole.title) == null ? '' : String(newRole.title)).trim() || !((newRole.description) == null ? '' : String(newRole.description)).trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/role/create`, newRole, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchRoles();
      setSelectedRole({ role_id: res.data.role_id, title: newRole.title });
      setNewRole({ title: '', description: '', requirements: '', nice_to_have: '' });
      setView('start');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error creating role');
    } finally {
      setLoading(false);
    }
  };

  const startEditRole = async (roleId) => {
    try {
      const res = await axios.get(`${API}/role/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewRole({
        title: res.data.title || '',
        description: res.data.description || '',
        requirements: res.data.requirements || '',
        nice_to_have: res.data.nice_to_have || '',
      });
      setEditingRoleId(roleId);
      setView('create-role');
    } catch (err) {
      alert(err.response?.data?.detail || 'Functie kon niet geladen worden');
    }
  };

  const updateRole = async () => {
    if (!((newRole.title) == null ? '' : String(newRole.title)).trim() || !((newRole.description) == null ? '' : String(newRole.description)).trim() || !editingRoleId) return;
    setLoading(true);
    try {
      await axios.put(`${API}/role/${editingRoleId}`, newRole, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchRoles();
      setSelectedRole({ role_id: editingRoleId, title: newRole.title });
      setNewRole({ title: '', description: '', requirements: '', nice_to_have: '' });
      setEditingRoleId(null);
      setView('start');
    } catch (err) {
      alert(err.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setLoading(false);
    }
  };

  const analyze = async () => {
    if (!((cvText) == null ? '' : String(cvText)).trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/analyze`, {
        cv_text: cvText,
        role_id: selectedRole?.role_id,
        session_id: sessionId,
        language: lang
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionId(res.data.session_id);
      setMatchScore(res.data.match_score);
      setMessages([
        { role: 'user', content: `Analyseer CV voor ${selectedRole?.title || 'algemene analyse'}` },
        { role: 'assistant', content: res.data.analysis }
      ]);
      setView('analysis');
    } catch (err) {
      alert(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const sendChat = async (msg) => {
    if (!msg?.trim() || !sessionId) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: msg,
        language: lang
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: (lang === 'en' ? 'Error: ' : 'Fout: ') + (err.response?.data?.detail || (lang === 'en' ? 'Request failed' : 'Verzoek mislukt')) }]);
    } finally {
      setLoading(false);
    }
  };

  const addCandidate = () => {
    if (!((newCandidateCv) == null ? '' : String(newCandidateCv)).trim()) return;
    const name = ((newCandidateName) == null ? '' : String(newCandidateName)).trim() || `Kandidaat ${candidates.length + 1}`;
    setCandidates(prev => [...prev, {
      id: Date.now().toString(),
      name,
      cvText: ((newCandidateCv) == null ? '' : String(newCandidateCv)).trim()
    }]);
    setNewCandidateName('');
    setNewCandidateCv('');
  };

  const removeCandidate = (id) => {
    setCandidates(prev => (Array.isArray(prev) ? prev : []).filter(c => c.id !== id));
  };

  const runComparison = async () => {
    if (candidates.length < 2) return;
    setLoading(true);
    setComparisonResult(null);
    try {
      const res = await axios.post(`${API}/compare`, {
        candidates: (Array.isArray(candidates) ? candidates : []).map(c => ({ cv_text: c.cvText })),
        role_id: selectedRole?.role_id || null,
        language: lang,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparisonResult(res.data.comparison);
    } catch (err) {
      alert(err.response?.data?.detail || 'Comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setView('start');
    setSessionId(null);
    setCvText('');
    setMessages([]);
    setMatchScore(null);
    setCandidates([]);
    setComparisonResult(null);
  };

  const handleFileUpload = async (file, target) => {
    if (!file) return;
    const allowed = ['.pdf', '.docx', '.txt'];
    const ext = ((file.name) == null ? '' : String(file.name)).toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!(Array.isArray(allowed) ? allowed : []).includes(ext)) {
      alert(lang === 'nl' ? 'Ondersteunde formaten: PDF, DOCX, TXT' : 'Supported formats: PDF, DOCX, TXT');
      return;
    }
    const setUpl = target === 'main' ? setUploading : setUploadingCompare;
    setUpl(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/cv/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (target === 'main') {
        setCvText(res.data.cv_text);
      } else {
        setNewCandidateCv(res.data.cv_text);
      }
    } catch (err) {
      alert(err.response?.data?.detail || (lang === 'nl' ? 'Upload mislukt' : 'Upload failed'));
    }
    setUpl(false);
  };

  const onDrop = (e, target) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileUpload(file, target);
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };

  // Create / Edit role view
  if (view === 'create-role') {
    const isEditing = !!editingRoleId;
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => { setView('start'); setEditingRoleId(null); setNewRole({ title: '', description: '', requirements: '', nice_to_have: '' }); }} className="btn-secondary">
            <ArrowLeft size={16} /> {txt.back}
          </button>
          <h1 className="heading-1">{isEditing ? txt.editRole : txt.createRole}</h1>
        </div>
        <div className="tool-content" style={{ maxWidth: '700px' }}>
          <div className="auto-input-card">
            <div className="form-group">
              <label className="body-small" style={{ fontWeight: 600 }}>{txt.roleTitle} *</label>
              <input type="text" className="form-input" placeholder={txt.roleTitlePlaceholder}
                value={newRole.title} onChange={(e) => setNewRole({ ...newRole, title: e.target.value })} data-testid="role-title-input" />
            </div>
            <div className="form-group">
              <label className="body-small" style={{ fontWeight: 600 }}>{txt.roleDesc} *</label>
              <textarea className="form-input auto-textarea" placeholder={txt.roleDescPlaceholder}
                value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} rows={4} data-testid="role-desc-input" />
            </div>
            <div className="form-group">
              <label className="body-small" style={{ fontWeight: 600 }}>{txt.requirements}</label>
              <textarea className="form-input auto-textarea" placeholder={txt.requirementsPlaceholder}
                value={newRole.requirements} onChange={(e) => setNewRole({ ...newRole, requirements: e.target.value })} rows={3} data-testid="role-req-input" />
            </div>
            <div className="form-group">
              <label className="body-small" style={{ fontWeight: 600 }}>{txt.niceToHave}</label>
              <textarea className="form-input auto-textarea" placeholder={txt.niceToHavePlaceholder}
                value={newRole.nice_to_have} onChange={(e) => setNewRole({ ...newRole, nice_to_have: e.target.value })} rows={2} data-testid="role-nice-input" />
            </div>
            <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}
              onClick={isEditing ? updateRole : createRole}
              disabled={loading || !((newRole.title) == null ? '' : String(newRole.title)).trim() || !((newRole.description) == null ? '' : String(newRole.description)).trim()}
              data-testid="save-role-btn">
              {loading ? <Loader className="spin" size={16} /> : <CheckCircle size={16} />}
              {isEditing ? txt.saveChanges : txt.saveRole}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compare view
  if (view === 'compare') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => setView('start')} className="btn-secondary" data-testid="back-to-start-btn">
            <ArrowLeft size={16} /> {txt.backToStart}
          </button>
          <h1 className="heading-1">
            <GitCompare size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
            {txt.compareTitle}
          </h1>
        </div>
        <div className="tool-content">
          <p className="body-medium text-secondary" style={{ marginBottom: '1.5rem', maxWidth: '600px' }}>
            {txt.compareSubtitle}
          </p>

          {selectedRole && (
            <div className="compass-role-badge" style={{ marginBottom: '1.5rem', background: 'var(--accent-blue-200)' }}>
              <Target size={14} />
              <span className="body-small">{lang === 'nl' ? 'Functie' : 'Role'}: <strong>{selectedRole.title}</strong></span>
            </div>
          )}

          <div className="auto-start-section" data-testid="compare-section">
            {/* Add candidate form */}
            <div className="auto-input-card" data-testid="add-candidate-form">
              <div className="auto-input-header">
                <Plus size={20} />
                <h3 className="heading-3">{txt.addCandidate}</h3>
              </div>
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label className="body-small" style={{ fontWeight: 600 }}>{txt.candidateName}</label>
                <input type="text" className="form-input" placeholder={txt.candidateNamePlaceholder}
                  value={newCandidateName} onChange={(e) => setNewCandidateName(e.target.value)} data-testid="candidate-name-input" />
              </div>
              <div className="form-group">
                <label className="body-small" style={{ fontWeight: 600 }}>{txt.candidateCv} *</label>
                {/* Upload zone for compare */}
                <div
                  onDrop={(e) => onDrop(e, 'compare')}
                  onDragOver={onDragOver}
                  data-testid="compare-cv-upload-zone"
                  style={{
                    border: '2px dashed rgba(255,255,255,0.12)', borderRadius: 10,
                    padding: '1rem', textAlign: 'center', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.02)', marginBottom: '0.5rem',
                  }}
                  onClick={() => compareCvFileRef.current?.click()}
                >
                  <input ref={compareCvFileRef} type="file" accept=".pdf,.docx,.txt" hidden
                    onChange={(e) => handleFileUpload(e.target.files?.[0], 'compare')}
                    data-testid="compare-cv-file-input" />
                  {uploadingCompare ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <Loader className="spin" size={16} />
                      <span className="body-small">{txt.uploading || 'Verwerken...'}</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <Upload size={18} style={{ color: 'var(--text-secondary)' }} />
                      <span className="body-small" style={{ fontWeight: 600 }}>{txt.uploadCandidateBtn || 'Upload CV'}</span>
                      <span className="caption text-secondary">(PDF, DOCX, TXT)</span>
                    </div>
                  )}
                </div>
                <textarea className="form-input auto-textarea" placeholder={txt.candidateCvPlaceholder}
                  value={newCandidateCv} onChange={(e) => setNewCandidateCv(e.target.value)} rows={5} data-testid="candidate-cv-input" />
              </div>
              <button className="btn-primary" style={{ width: '100%' }}
                onClick={addCandidate} disabled={!((newCandidateCv) == null ? '' : String(newCandidateCv)).trim()} data-testid="add-candidate-btn">
                <Plus size={16} /> {txt.addBtn}
              </button>
            </div>

            {/* Candidates list */}
            <div className="auto-input-card" data-testid="candidates-list">
              <div className="auto-input-header">
                <Users size={20} />
                <h3 className="heading-3">{txt.candidatesList} ({candidates.length})</h3>
              </div>
              {candidates.length === 0 ? (
                <p className="body-small text-secondary" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
                  {txt.noCandidates}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
                  {(Array.isArray(candidates) ? candidates : []).map((c, idx) => (
                    <div key={c.id} className="compass-nav-item" style={{ justifyContent: 'space-between', padding: '0.75rem 1rem' }}
                      data-testid={`candidate-item-${idx}`}>
                      <div style={{ flex: 1 }}>
                        <span className="body-small" style={{ fontWeight: 600 }}>{c.name}</span>
                        <span className="caption text-secondary" style={{ marginLeft: '0.5rem' }}>
                          ({c.cvText.length} {lang === 'nl' ? 'tekens' : 'chars'})
                        </span>
                      </div>
                      <button onClick={() => removeCandidate(c.id)} className="btn-icon-sm" data-testid={`remove-candidate-${idx}`}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button className="btn-primary" style={{ width: '100%', marginTop: '1.5rem' }}
                onClick={runComparison} disabled={loading || candidates.length < 2} data-testid="compare-candidates-btn">
                {loading ? <><Loader className="spin" size={16} /> {txt.comparing}</> : <><GitCompare size={16} /> {txt.compareBtn}</>}
              </button>
            </div>

            {/* Comparison result */}
            {comparisonResult && (
              <div className="auto-input-card" style={{ background: 'var(--bg-section)' }} data-testid="comparison-result">
                <div className="auto-input-header">
                  <Star size={20} />
                  <h3 className="heading-3">{txt.comparisonResult}</h3>
                </div>
                <div className="markdown-content" style={{ marginTop: '1rem' }}>
                  <ReactMarkdown>{comparisonResult}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Start view
  if (view === 'start') {
    return (
      <div className="tool-page">
        <div className="tool-header">
          <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary">
            <ArrowLeft size={16} /> {t.aiTools}
          </button>
          <h1 className="heading-1">
            <UserCheck size={22} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8 }} />
            {txt.title}
          </h1>
        </div>
        <div className="tool-content">
          <p className="body-medium text-secondary" style={{ marginBottom: '2rem', maxWidth: '600px' }}>
            {txt.subtitle}
          </p>
          <div className="auto-start-section" data-testid="hiring-start">
            {/* Step 1: Role selection */}
            <div className="auto-input-card" data-testid="role-selection">
              <div className="auto-input-header">
                <Briefcase size={20} />
                <h3 className="heading-3">{txt.step1}</h3>
              </div>
              <p className="body-small text-secondary">{txt.step1Desc}</p>
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}
                onClick={() => setView('create-role')} data-testid="create-role-btn">
                <Plus size={16} /> {txt.createRole}
              </button>
              {roles.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 className="body-small" style={{ fontWeight: 600, marginBottom: '0.75rem' }}>{txt.existingRoles}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {(Array.isArray(roles) ? roles : []).map(role => (
                      <button key={role.role_id}
                        className={`compass-nav-item ${selectedRole?.role_id === role.role_id ? 'active' : ''}`}
                        onClick={() => setSelectedRole(role)} style={{ justifyContent: 'flex-start' }} data-testid={`role-${role.role_id}`}>
                        <Briefcase size={14} />
                        <span style={{ flex: 1, textAlign: 'left' }}>{role.title}</span>
                        {selectedRole?.role_id === role.role_id && <CheckCircle size={14} style={{ color: 'var(--accent-green)' }} />}
                        <span onClick={(e) => { e.stopPropagation(); startEditRole(role.role_id); }}
                          style={{ marginLeft: '0.25rem', opacity: 0.5, cursor: 'pointer' }}
                          title={lang === 'nl' ? 'Bewerken' : 'Edit'}
                          data-testid={`edit-role-${role.role_id}`}>
                          <Pencil size={13} />
                        </span>
                        <span onClick={(e) => { e.stopPropagation(); deleteRole(role.role_id); }}
                          style={{ marginLeft: '0.25rem', opacity: 0.5, cursor: 'pointer' }}
                          data-testid={`delete-role-${role.role_id}`}>
                          <Trash2 size={13} />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {roles.length === 0 && (
                <p className="caption text-secondary" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
                  {txt.noRoles}
                </p>
              )}
            </div>

            {/* Step 2: Single CV */}
            <div className="auto-input-card" data-testid="cv-input">
              <div className="auto-input-header">
                <FileText size={20} />
                <h3 className="heading-3">{txt.step2}</h3>
              </div>
              <p className="body-small text-secondary">{txt.step2Desc}</p>
              {selectedRole && (
                <div className="compass-role-badge" style={{ marginTop: '1rem', background: 'var(--accent-blue-200)' }}>
                  <Target size={14} />
                  <span className="body-small">{lang === 'nl' ? 'Geselecteerde functie' : 'Selected role'}: <strong>{selectedRole.title}</strong></span>
                </div>
              )}

              {/* Upload zone */}
              <div
                onDrop={(e) => onDrop(e, 'main')}
                onDragOver={onDragOver}
                data-testid="cv-upload-zone"
                style={{
                  marginTop: '1rem', border: '2px dashed rgba(255,255,255,0.12)', borderRadius: 10,
                  padding: '1.25rem', textAlign: 'center', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.02)', transition: 'border-color 0.2s',
                }}
                onClick={() => cvFileRef.current?.click()}
              >
                <input ref={cvFileRef} type="file" accept=".pdf,.docx,.txt" hidden
                  onChange={(e) => handleFileUpload(e.target.files?.[0], 'main')}
                  data-testid="cv-file-input" />
                {uploading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Loader className="spin" size={18} />
                    <span className="body-small">{txt.uploading}</span>
                  </div>
                ) : (
                  <>
                    <Upload size={24} style={{ color: 'var(--text-secondary)', marginBottom: 6 }} />
                    <p className="body-small" style={{ fontWeight: 600 }}>{txt.uploadBtn}</p>
                    <p className="caption text-secondary">{txt.uploadHint} &middot; {txt.uploadDrag}</p>
                  </>
                )}
              </div>

              <p className="caption text-secondary" style={{ textAlign: 'center', margin: '0.5rem 0' }}>{txt.orPaste}</p>

              <textarea className="form-input auto-textarea" placeholder={txt.cvPlaceholder}
                value={cvText} onChange={(e) => setCvText(e.target.value)} rows={6}
                data-testid="cv-textarea" />
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}
                onClick={analyze} disabled={loading || !((cvText) == null ? '' : String(cvText)).trim()} data-testid="analyze-btn">
                {loading ? <Loader className="spin" size={16} /> : <UserCheck size={16} />}
                {txt.analyzeBtn}
              </button>
            </div>

            {/* Compare multiple CVs section */}
            <div className="auto-input-card" data-testid="compare-section-start" style={{ background: 'var(--bg-section)' }}>
              <div className="auto-input-header">
                <GitCompare size={20} />
                <h3 className="heading-3">{txt.compareTitle}</h3>
              </div>
              <p className="body-small text-secondary">{txt.compareSubtitle}</p>
              <button className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}
                onClick={() => setView('compare')} data-testid="start-comparison-btn">
                <Users size={16} /> {txt.startComparison}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Analysis view (single CV)
  return (
    <div className="compass-coach-page" data-testid="hiring-workspace">
      <aside className="compass-sidebar">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" style={{ width: '100%', justifyContent: 'center' }}>
          <ArrowLeft size={14} /> {t.aiTools}
        </button>
        <div className="compass-role-badge" style={{ background: 'var(--accent-blue-200)' }}>
          <UserCheck size={16} />
          <span className="body-small" style={{ fontWeight: 500 }}>{txt.title}</span>
        </div>
        {selectedRole && (
          <div className="compass-role-badge" style={{ background: 'var(--bg-section)' }}>
            <Briefcase size={14} />
            <span className="body-small">{selectedRole.title}</span>
          </div>
        )}
        {matchScore !== null && (
          <div className="sim-sidebar-stats" data-testid="match-stats">
            <div className="sim-stat-item" style={{
              background: matchScore >= 70 ? 'var(--accent-green-200)' : matchScore >= 50 ? 'var(--accent-orange-200)' : 'var(--accent-pink-200)'
            }}>
              <Star size={14} />
              <span className="body-small">{txt.matchScore}</span>
              <span className="body-small" style={{ fontWeight: 700, fontSize: '1.1rem' }}>{matchScore}/100</span>
            </div>
          </div>
        )}
        <button className="compass-new-chat" onClick={startNew} data-testid="new-analysis-btn">
          <Plus size={14} /> {txt.newAnalysis}
        </button>
      </aside>
      <main className="compass-main">
        <div className="compass-chat-container">
          <div className="compass-chat-messages" data-testid="hiring-messages">
            {(Array.isArray(messages) ? messages : []).map((msg, i) => (
              <div key={i} className={`compass-msg compass-msg-${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <div className="compass-msg-content markdown-content"><ReactMarkdown>{msg.content}</ReactMarkdown></div>
                ) : (
                  <div className="compass-msg-content">{msg.content}</div>
                )}
              </div>
            ))}
            {loading && (
              <div className="compass-msg compass-msg-assistant">
                <div className="compass-msg-content compass-typing">
                  <Loader className="spin" size={16} /> {lang === 'nl' ? 'Analyseren...' : 'Analyzing...'}
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          {messages.length <= 2 && !loading && (
            <div className="auto-followup-bar" data-testid="hiring-suggestions">
              {(Array.isArray(txt.suggestions) ? txt.suggestions : []).map((s, i) => (
                <button key={i} className="compass-suggestion-btn" onClick={() => sendChat(s)}>
                  <Lightbulb size={14} /> {s}
                </button>
              ))}
            </div>
          )}
          <form className="compass-chat-input" onSubmit={(e) => { e.preventDefault(); sendChat(input); }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder={txt.chatPlaceholder} disabled={loading} className="form-input" data-testid="hiring-input" />
            <button type="submit" disabled={loading || !((input) == null ? '' : String(input)).trim()} className="btn-primary compass-send-btn">
              <Send size={16} />
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default HiringAssistant;
